import { normalizeClientGuid, stripGuidBraces } from '@brighthub/uisession';

export { normalizeClientGuid };

const MAX_NAME_LEN = 200;
const MAX_GUID_LEN = 50;

/** Standard UUID, optional braces stripped before test. */
const UUID_PATTERN =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/** 32-char hex without hyphens (common alternate encoding). */
const HEX32_PATTERN = /^[0-9a-fA-F]{32}$/;

/** Salesforce anchor IDs exported from CRM (15- or 18-char alphanumeric). */
const SALESFORCE_ANCHOR_PATTERN = /^[a-zA-Z0-9]{15}(?:[a-zA-Z0-9]{3})?$/;

export { stripGuidBraces };

/**
 * Heuristic for prototype client search `type=guid`.
 * Matches standard UUIDs, 32-char hex, and Salesforce-style anchor IDs.
 */
export function looksLikeClientSearchGuid(raw: string): boolean {
  const x = stripGuidBraces(raw);
  if (!x || x.length > MAX_GUID_LEN) {
    return false;
  }
  return (
    UUID_PATTERN.test(x) ||
    HEX32_PATTERN.test(x) ||
    SALESFORCE_ANCHOR_PATTERN.test(x)
  );
}

/**
 * Normalizes a GUID-shaped search query for the prototype search API.
 * Salesforce anchor IDs keep original casing; UUID-like values are lowercased.
 */
function clientSearchGuidQuery(raw: string): string {
  const stripped = stripGuidBraces(raw.trim());
  if (SALESFORCE_ANCHOR_PATTERN.test(stripped)) {
    return stripped;
  }
  return normalizeClientGuid(raw) ?? stripped;
}

export type ClientSearchPrepared =
  | { kind: 'skip' }
  | { kind: 'call'; searchType: 'name' | 'guid'; qForApi: string };

/**
 * Prepares `q` and search mode for GET /v1/prototype/client/search.
 * Skips API calls when input is empty or name-only wildcards/spaces.
 */
export function prepareClientSearch(raw: string): ClientSearchPrepared {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { kind: 'skip' };
  }

  if (looksLikeClientSearchGuid(trimmed)) {
    return {
      kind: 'call',
      searchType: 'guid',
      qForApi: clientSearchGuidQuery(trimmed),
    };
  }

  const normalizedSpaces = trimmed.replace(/\s+/g, ' ');
  const capped = normalizedSpaces.slice(0, MAX_NAME_LEN);
  const withoutWildcardsAndSpace = capped.replace(/\*/g, '').replace(/\s/g, '');
  if (withoutWildcardsAndSpace.length === 0) {
    return { kind: 'skip' };
  }

  return { kind: 'call', searchType: 'name', qForApi: capped };
}
